import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Key, Mail, ArrowRight, Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';

import { Navbar } from '../../../components/Navbar.jsx';
import { api } from '@/lib/api.ts';

// --- FONDO AMBIENTAL ---
const AmbientBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none bg-[#020202]">
    <div className="absolute top-[-10%] left-[20%] w-[60vw] h-[60vw] bg-indigo-900/20 rounded-full blur-[150px] opacity-30 mix-blend-screen animate-pulse" />
    <div className="absolute bottom-[-10%] right-[20%] w-[50vw] h-[50vw] bg-cyan-900/10 rounded-full blur-[150px] opacity-20 mix-blend-screen" />
  </div>
);

export function RecuperarPasswordPage() {
  const [emailSent, setEmailSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm({
    defaultValues: {
      email: '',
    },
  });

  const recoverPassword = useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post('/api/auth/forgot-password', payload);
      return data;
    },
    onSuccess: () => {
      setEmailSent(true);
      toast.success('Enlace enviado correctamente.');
    },
    onError: (error) => {
      const message = error.response?.data?.detail ?? 'No se pudo enviar el email';
      toast.error(message);
    },
  });

  const onSubmit = (values) => {
    recoverPassword.mutate(values);
  };

  // Estilos reutilizables
  const inputContainerClass = "relative group";
  const inputClass = "w-full h-12 bg-black/40 border border-white/10 rounded-xl pl-11 pr-4 text-sm text-white placeholder-white/20 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/20 transition-all";
  const iconClass = "absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/30 group-focus-within:text-indigo-400 transition-colors";

  // --- VISTA DE ÉXITO (EMAIL ENVIADO) ---
  if (emailSent) {
    return (
      <div className="h-screen w-full text-slate-200 font-sans selection:bg-indigo-500/30 flex flex-col overflow-hidden">
        <AmbientBackground />
        <div className="flex-shrink-0 z-50"><Navbar /></div>

        <main className="flex-1 flex items-center justify-center px-4 relative">
          <div className="w-full max-w-md relative z-10">
            <div className="rounded-[2.5rem] border border-white/10 bg-[#0F0F0F]/80 backdrop-blur-xl shadow-2xl p-8 md:p-10 text-center relative overflow-hidden">
              
              {/* Decoración Éxito */}
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent opacity-60" />
              
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6 animate-in zoom-in duration-300">
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              </div>

              <h1 className="text-2xl font-bold text-white mb-3">¡Enlace Enviado!</h1>
              
              <p className="text-white/60 text-sm leading-relaxed mb-2">
                Hemos enviado las instrucciones de recuperación a:
              </p>
              <div className="bg-white/5 border border-white/5 rounded-lg py-2 px-4 mb-6 inline-block">
                <p className="text-emerald-400 font-mono text-sm">{getValues('email')}</p>
              </div>

              <p className="text-[10px] text-white/30 mb-8 max-w-xs mx-auto">
                Revisa tu bandeja de entrada (y spam). El enlace expirará pronto.
              </p>

              <div className="space-y-3">
                <Link
                  to="/inicio-sesion"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-white text-black font-bold uppercase tracking-widest text-[10px] hover:bg-slate-200 transition-all shadow-lg"
                >
                  Volver al Login
                </Link>
                <button
                  onClick={() => setEmailSent(false)}
                  className="text-[10px] font-bold uppercase tracking-widest text-white/40 hover:text-white transition-colors"
                >
                  ¿No llegó? Intentar de nuevo
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // --- VISTA FORMULARIO ---
  return (
    <div className="h-screen w-full text-slate-200 font-sans selection:bg-indigo-500/30 flex flex-col overflow-hidden">
      <AmbientBackground />
      
      <div className="flex-shrink-0 z-50">
        <Navbar />
      </div>

      <main className="flex-1 flex items-center justify-center px-4 relative">
        
        <div className="w-full max-w-md relative z-10">
          
          {/* Botón Volver Flotante */}
          <div className="mb-4">
             <Link to="/inicio-sesion" className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-white/40 hover:text-white transition-colors">
                <ArrowLeft className="h-3 w-3" /> Volver
             </Link>
          </div>

          <div className="rounded-[2.5rem] border border-white/10 bg-[#0F0F0F]/80 backdrop-blur-xl shadow-2xl overflow-hidden relative p-8 md:p-10">
            
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent opacity-60" />

            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-indigo-500/10 border border-cyan-500/20 shadow-[0_0_20px_rgba(6,182,212,0.2)] mb-4">
                <Key className="w-6 h-6 text-cyan-400" />
              </div>
              <h1 className="text-2xl font-bold text-white tracking-tight mb-2">Recuperar Acceso</h1>
              <p className="text-white/50 text-xs leading-relaxed max-w-xs mx-auto">
                Ingresa tu correo y te enviaremos un enlace seguro.
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-white/40 mb-2 ml-1">
                  Correo Electrónico
                </label>
                <div className={inputContainerClass}>
                  <Mail className={iconClass} />
                  <input
                    type="email"
                    placeholder="tu@email.com"
                    className={inputClass}
                    {...register('email', {
                      required: 'Requerido',
                      pattern: { value: /^\S+@\S+$/i, message: 'Email inválido' }
                    })}
                  />
                </div>
                {errors.email && <p className="text-xs text-red-400 mt-1 ml-1">{errors.email.message}</p>}
              </div>

              <button
                type="submit"
                disabled={recoverPassword.isPending}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-cyan-600 to-indigo-600 hover:from-cyan-500 hover:to-indigo-500 text-white font-bold uppercase tracking-widest text-[10px] shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all transform hover:scale-[1.02] active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed border border-white/10"
              >
                {recoverPassword.isPending ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" /> Procesando...
                  </>
                ) : (
                  <>
                    Enviar Enlace <ArrowRight className="h-3 w-3" />
                  </>
                )}
              </button>
            </form>

          </div>
        </div>
      </main>
    </div>
  );
}